/* eslint-disable max-len */
import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { builder, Builder, BuilderComponent } from '@builder.io/react/lite';

import Head from 'next/head';
import getConfig from 'next/config';
import NextLink from 'next/link';

import BuilderPageMetadata from '@glowforge/builder-components/lib/builder-page-metadata';
import registerComponents from '@glowforge/builder-components/lib/register';
import HeadTitle from '../components/head/head-title';
import ModalPriorityContext from '../context/ModalPriorityContext/ModalPriorityContext';
import ShareProgramContext, { parseReferralDiscount } from '../context/ShareProgramContext';
import LocationContext from '../context/LocationContext/LocationContext';
import useFeatureFlags from '../hooks/use-feature-flags';
import { fetchProducts } from '../api/storefront';
import { REACT_ROOT } from '../constants';
import Canonical from './canonical';
import { optimizelyTrackExperience, builderTrackExperience } from './tracking';
import FriendbuyAuthScript from './friendbuy-scripts';

/**
 * Returns the Builder Model objects so we can create page
 * representations consistently
 * @param {Object} props
 * @param {string} props.model
 * @param {string} props.pathPrefix This should not have a leading or trailing slash. ie "products/pro" not "/products/pro/"
 * @param {boolean} props.noIndex
 * @param {array} props.components
 * @returns
 */
// eslint-disable-next-line default-param-last
function builderPathInit({ model, pathPrefix, noIndex = false, components }) {
  builder.init('69fac0673d2644f0a0a962e1cb3045ef');
  Builder.register('editor.settings', {
    customInsertMenu: true,
  });
  registerComponents(Builder, components);

  const ALLOWED_CACHE_HEADERS = {
    sMaxage: 's-maxage',
    staleWhileRevalidate: 'stale-while-revalidate',
  };

  const {
    publicRuntimeConfig: {
      GF_DOTCOM_ENV,
      GF_DOTCOM_SHOPIFY_URL,
    },
  } = getConfig();

  /**
   * If prod env, only render explicitly production pages.
   * Staging and development envs can view all pages.
   */
  const builderComponentQuery =
    GF_DOTCOM_ENV === 'production'
      ? {
          query: {
            data: {
              environment: GF_DOTCOM_ENV ?? 'production',
            },
          },
        }
      : {};

  /**
   * @param {object} props
   * @param {string} [props.href]
   * @param {string} [props.target]
   * @returns
   */
  function renderLink(props) {
    // nextjs link doesn't handle hash links well if it's on the same page
    // (starts with #)
    if (props.target === '_blank' || props.href?.startsWith('#')) {
      return <a {...props}>{props.children}</a>;
    }
    const { children, href, ...rest } = props;
    return (
      <NextLink href={href}>
        <a {...rest}>{children}</a>
      </NextLink>
    );
  }

  /**
   * Tracks the A/B variations in builder
   * @param {*} data
   * @param {*} content
   */
  function trackVariation(data, content) {
    optimizelyTrackExperience();
    builderTrackExperience(content);
  }


  /**
   * BuilderPage
   * If page not found, then it renders 404 page. 404.jsx contains
   * code to render preview of builder content that has yet to be published.
   */
  function BuilderPage({
    content,
    title,
    includeNav,
    includeFooter,
    products,
    fetchReferralData,
    additionalData,
  }) {
    // store data in local state, so we can update and pass to BuilderComponent
    // when it updates
    const [data, setData] = useState({
      ...additionalData,
      includeNav,
      includeFooter,
      products,
    });

    const [loadedContent, setLoadedContent] = useState({});
    const [currentReferral, setCurrentReferral] = useState({});
    const [currentAffiliate, setCurrentAffiliate] = useState({});

    const referralsFlag = (useFeatureFlags())['faster-friends'];

    useEffect(() => {
      // Ensure that we're adding permissions for Osan
      //  in order to be GDPR compliant
      builder.canTrack =
        window.Osano &&
        window.Osano.cm &&
        window.Osano.cm.getConsent &&
        window.Osano.cm.getConsent().ANALYTICS === 'ACCEPT';
    }, []);

    useEffect(() => {
      if (Object.keys(loadedContent).length) {
        trackVariation(loadedContent?.data, loadedContent?.content);
      }

      if (loadedContent?.data?.navbarButtonUrl) {
        const buyButtons = document.querySelectorAll('.navbarButton a[href]');
        buyButtons.forEach((buyButton) => {
          if(buyButton){
            buyButton.setAttribute('href', loadedContent.data.navbarButtonUrl);
          }
        });
      }
    }, [loadedContent]);

    const { country: countryCode, hasFetched: hasFetchedLocation } =
      useContext(LocationContext);

    const { referralInfo, affiliateInfo, getGFReferralInfo } =
      useContext(ShareProgramContext);

    useEffect(() => {
      if (fetchReferralData && hasFetchedLocation && countryCode !== 'GB') {
        /**
         * To test this, you can do either 1 or 2:
         *  1. in your local .env, set GF_DOTCOM_USE_DEV_PROXY=true
         *    a. This will forward requests to marketing-rails-staging.glowforge.com
         *    b. go to marketing-rails-staging.glowforge.com/fod to set an affiliate link
         *    c. todo: test against actual referral (not affiliate)
         *  2. In builder state data, manually change these values
         *    a. https://www.builder.io/c/docs/guides/state-and-actions
         */
        getGFReferralInfo();

        const {
          code = '',
          referrerName = '',
          photoUrl = '',
          pl: { id = '' } = {},
          shopUrl = `${GF_DOTCOM_SHOPIFY_URL}/collections/printers`,
        } = referralInfo || {};

        // Referral code takes precedence over promotional link
        setCurrentReferral({
          name: referrerName,
          photoUrl,
          referralCode: code || id,
          shopUrl,
          ...parseReferralDiscount(products),
        });

        setCurrentAffiliate({
          name: affiliateInfo?.name || '',
          id: affiliateInfo?.code || '',
        });
      }
    }, [
      affiliateInfo,
      countryCode,
      getGFReferralInfo,
      fetchReferralData,
      hasFetchedLocation,
      products,
      referralInfo,
      setData,
    ]);

    if (!content) return null;

    return (
      <>
        {referralsFlag ? <FriendbuyAuthScript /> : null}
        {content?.data ? <BuilderPageMetadata {...content?.data} /> : null}
        {!!content?.data && title ? <HeadTitle title={title} /> : null}
        {content?.data?.noindex || noIndex ? (
          <Head>
            <meta name='robots' content='noindex' />
          </Head>
        ) : null}
        {content?.data?.url ? <Canonical path={content.data.url} /> : null}
        <BuilderComponent
          content={content}
          contentLoaded={(builderData, builderContent) => {
            setLoadedContent({ data: builderData, content: builderContent });
          }}
          model={model}
          renderLink={renderLink}
          options={{
            includeRefs: true,
            noTargeting: true,
          }}
          context={{
            // Used to coordinate builder.io modals with built-in modals
            ModalPriorityContext,
            REACT_ROOT,
          }}
          data={{
            referrals: currentReferral,
            affiliates: currentAffiliate,
            ...data,
          }}
        />
      </>
    );
  }

  async function getServerSideProps(context) {
    const { params, res } = context;
    // account for empty path prefix

    const prefix = pathPrefix === '' ? '' : `/${pathPrefix}`;
    const urlPath = `${prefix}/${params?.slug?.join('/') ?? ''}`;

    if (pathPrefix[0] === '/' || pathPrefix[pathPrefix.length - 1] === '/') {
      console.error(
        'builderPathInit pathPrefix arg should not have a leading or trailing slash.'
      );
    }

    const content = await builder
      .get(model, {
        userAttributes: {
          urlPath,
          ...builderComponentQuery,
        },
      })
      .promise();

    if (!content) {
      return {
        notFound: true,
      };
    }

    const {
      title = null,
      cacheControl,
      includeNav = true,
      includeFooter = true,
      hideBuyButton = false,
      fetchProductData = false,
      fetchReferralData = false,
    } = content.data;

    let products = null;
    if (fetchProductData) {
      products = await fetchProducts();
    }

    /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~EXPERIMENTAL~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    This is an experimental method to dynamically set cache-control via CMS
    response.
    I'm not entirely sure how I feel about doing it this way.
    On the plus side, it allows rapid, no-code changes to cacheing on a
    per-page basis.
    On the downside, it abstracts cacheing logic across two (at minimum)
    layers - CMS & code

    Thoughts?
    /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
    if (cacheControl && Object.keys(cacheControl).length > 0) {
      // Iterate through the Cache Control obj and construct an array of
      // header values.
      // Cache-Control format relies on specific dash locations, so we grab this
      // from lookup table.
      // We then create a comma-separated string from the key-value pairs.
      const cacheControlHeader = [];

      Object.entries(cacheControl).forEach(([key, value]) => {
        if (typeof value === 'number' && key in ALLOWED_CACHE_HEADERS) {
          cacheControlHeader.push(
            `${ALLOWED_CACHE_HEADERS[key]}=${value > 3600 ? 3600 : value}`
          );
        }
      });

      res.setHeader('Cache-Control', cacheControlHeader.join(', '));
    }
    /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~END EXPERIMENT~~~~~~~~~~~~~~~~~~~~~~~ */
    return {
      props: {
        content,
        fetchReferralData,
        includeFooter,
        includeNav,
        hideBuyButton,
        products,
        title,
      },
    };
  }

  BuilderPage.propTypes = {
    content: PropTypes.shape({
      data: PropTypes.shape({
        noindex: PropTypes.bool,
        url: PropTypes.string.isRequired,
      }),
    }).isRequired,
    products: PropTypes.shape({}),
    fetchReferralData: PropTypes.bool,
    includeFooter: PropTypes.bool,
    includeNav: PropTypes.bool,
    hideBuyButton: PropTypes.bool,
    title: PropTypes.string,
    additionalData: PropTypes.shape({})
  };

  BuilderPage.defaultProps = {
    fetchReferralData: false,
    includeFooter: true,
    includeNav: true,
    hideBuyButton: false,
    title: null,
    products: null,
    additionalData: {},
  };

  return {
    BuilderPage,
    getServerSideProps,
  };
}

export default builderPathInit;
